'use strict';

namespace Core {

    class ScNav {

        // Private
        private element: JQuery;
        private jWindow: JQuery = $(this.$window);

        private items: ScNavItem[] = [];
        private activeItem: ScNavItem;
        private firstItem: ScNavItem;
        private lastItem: ScNavItem;

        private eventNamespace: string = `.${this.componentUtil.getRandomID('sc-nav')}`;

        private scrollOffset: number = 100;
        private scrollDuration: number = 400;

        private isStuck: boolean = false;

        constructor (
            private $scope: ng.IScope,
            private $window: ng.IWindowService,
            private $timeout: ng.ITimeoutService,
            private componentUtil: TR.ComponentUtil
        ) {
        }

        public init (element: ng.IAugmentedJQuery): void {

            this.element = element;

            this.firstItem = _.head(this.items);
            this.lastItem = _.last(this.items);

            // Include only the first item to the tab sequence
            this.firstItem.isTabbable = true;

            // set active item on scroll/resize
            let throttledScroll = _.throttle(() => this.setActiveItem(), 100);
            this.jWindow.on(`scroll${this.eventNamespace}`, () => throttledScroll());
            this.jWindow.on(`resize${this.eventNamespace}`, () => throttledScroll());

            // set initial active item
            this.setActiveItem();

            this.$scope.$on('$destroy', () => this.destroy());
        }

        public addItem (item: ScNavItem): void {
            this.items.push(item);
        }

        public setTabbableItem (item: ScNavItem): void {
            const tabbable: ScNavItem =  _.find(this.items, (item) => item.isTabbable);
            tabbable.isTabbable = false;
            item.isTabbable = true;
        }

        public setFocusToItem (item: ScNavItem): void {
            item.setFocus();
        }

        public setFocusToPreviousItem (item: ScNavItem): void {
            let next: ScNavItem =
                item === this.firstItem
                    ? this.lastItem
                    : this.items[this.items.indexOf(item) - 1];
            this.setFocusToItem(next);
        }

        public setFocusToNextItem (item: ScNavItem): void {
            let next: ScNavItem =
                item === this.lastItem
                    ? this.firstItem
                    : this.items[this.items.indexOf(item) + 1];
            this.setFocusToItem(next);
        }

        public setFocusTofirstItem (): void {
            this.setFocusToItem(this.firstItem);
        }

        public setFocusTolastItem (): void {
            this.setFocusToItem(this.lastItem);
        }

        private setActiveItem(): void {
            // We want to activate the last item whose target element has breached our scroll offset
            let item: ScNavItem =
                _.findLast(this.items, (item: ScNavItem, index: number) => {
                    // get top of item
                    const top = item.targetElement[0].getBoundingClientRect().top;
                    return top <= this.scrollOffset;
                });

            // if no item qualified then we are presumably above the first item so select that
            !item && (item = this.items[0]);

            if (item !== this.activeItem) {
                // Deselect the active item
                this.activeItem && (this.activeItem.isActive = false);
                this.$timeout(() => {
                    item.isActive = true;
                });
                this.activeItem = item;
            }
        }

        public scrollToItemTarget (item: ScNavItem): void {
            if (item.targetElement) {
                const top: number = (item.targetElement.offset().top - this.scrollOffset) + 1;
                $('html, body').animate({ scrollTop: top }, {duration: this.scrollDuration, queue: false});
                // TODO change focus to target after animation?
                //https://css-tricks.com/snippets/jquery/smooth-scrolling/
            }
        }

        public setStuck (value: boolean) {
            this.$timeout(() => {
                this.isStuck = value;
            });
        }

        private destroy (): void {
            this.jWindow.off(this.eventNamespace);
        }
    }

    class ScNavItem {

        // Bound
        heading: string;

        // Public
        public isActive: boolean = false;
        // Focus is managed using Roving tabindex
        // https://www.w3.org/TR/2017/WD-wai-aria-practices-1.1-20170628/#kbd_roving_tabindex
        // By default a nav item is focusable but not included in the tab sequence (tabindex="-1").
        // On page load the first nav item will be included in the tab sequence (tabindex="0").
        // If another item receives focus, its tabindex will be set to 0 and all other items will be set to -1.
        public isTabbable: boolean = false;

        // Private
        public element: JQuery;
        public targetElement: JQuery;
        private parentNav: ScNav;

        public top: number;

        constructor (
        ) {
        }

        private events = {
            'click': (event) => {
                event.preventDefault();
                // scroll to target
                this.parentNav.scrollToItemTarget(this);

            },
            'keydown': (event) => {
                switch (event.keyCode) {
                    case TR.KeyCode.SPACE:
                    case TR.KeyCode.RETURN:
                        this.parentNav.scrollToItemTarget(this);
                        break;
                    case TR.KeyCode.LEFT:
                        this.parentNav.setFocusToPreviousItem(this);
                        break;
                    case TR.KeyCode.RIGHT:
                        this.parentNav.setFocusToNextItem(this);
                        break;
                    case TR.KeyCode.HOME:
                        event.preventDefault();
                        this.parentNav.setFocusTolastItem();
                        break;
                    case TR.KeyCode.END:
                        event.preventDefault();
                        this.parentNav.setFocusTofirstItem();
                        break;
                    default:
                }
            },
            'focus': (event) => {
                this.parentNav.setTabbableItem(this);
            }
        };

        public init (
            element: ng.IAugmentedJQuery,
            targetElement: ng.IAugmentedJQuery,
            nav: ScNav
        ): void {
            this.element = element;
            if (targetElement.length === 0) {
                throw 'Target element not found for ScNavItem';
            }
            this.targetElement = targetElement;

            this.parentNav = nav;

            // Add item to it's parent
            this.parentNav.addItem(this);
        }

        public setFocus (): void {
            this.element.focus();
        }
    }

    angular
        .module('core')
        .directive('scInPageNav', (): ng.IDirective => {
            return {
                scope: {},
                restrict: 'E',
                replace: true,
                transclude: true,
                controller: ScNav,
                controllerAs: 'nav',
                template:
                    `<div>
                        <div sc-sticky-el
                            data-on-stick="nav.setStuck(true)"
                            data-on-unstick="nav.setStuck(false)"
                            data-sticky-class="u-sticky-bar">
                            <nav 
                                class="sc-c-tabs sc-c-tabs--centered sc-c-tabs--contained" 
                                ng-class="{'sc-c-tabs--dark sc-c-tabs--elevated': nav.isStuck}"
                                >
                                <div class="sc-c-tabs__scroller">
                                    <div class="sc-c-tabs__tablist"
                                        ng-transclude>
                                    </div>
                                </div>
                            </nav>
                        </div>
                    </div>`,
                link: (
                    scope : ng.IScope,
                    el: ng.IAugmentedJQuery,
                    attr: ng.IAttributes,
                    controller: ScNav
                ) => {
                    controller.init(el);
                }
            };
        })
        .directive('scInPageNavItem', (): ng.IDirective => {
            return {
                scope: {},
                restrict: 'E',
                require: '^scInPageNav',
                replace: true,
                transclude: true,
                controller: ScNavItem,
                controllerAs: 'item',
                bindToController: {
                    heading: '@',
                    targetId: '@'
                },
                template:
                    `<a class="sc-c-tab sc-c-tab--l" 
                        ng-class="{'is-selected': item.isActive}"
                        href="#{{item.target}}"
                        ng-click="item.events.click($event)"
                        ng-keydown="item.events.keydown($event)"
                        ng-focus="item.events.focus($event)"
                        ng-attr-tabindex="{{item.isTabbable ? '0' : '-1'}}">
                        <span class='sc-c-tab__label'>{{item.heading}}</span>
                     </a>
                    `,
                link: (
                    scope : ng.IScope,
                    el: ng.IAugmentedJQuery,
                    attr: ng.IAttributes,
                    navCtrl: ScNav
                ) => {
                    const target = $(`#${attr['targetId']}`);
                    scope.item.init(el, target, navCtrl);
                }
            };
        });
}

